import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput, SelectInput } from "@/components/FormKit";
import { opportunitiesApi, apiErrorMessage, isSuppressionOverride, productsApi } from "@/api/client";
import { OverrideNotice } from "@/components/forms/OverrideNotice";
import { toast } from "sonner";

export function OpportunityFormDialog({ open, onOpenChange, orgId, onSaved }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_code: "", source: "", estimated_value: "", probability: "", next_action: "", next_action_date: "" });
  const [override, setOverride] = useState("");
  const [needsOverride, setNeedsOverride] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ product_code: "", source: "", estimated_value: "", probability: "", next_action: "", next_action_date: "" });
      setOverride(""); setNeedsOverride(false); setError("");
      productsApi.list({ active_only: true }).then((r) => setProducts(r.data.items)).catch(() => {});
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_code) { setError("Choose a product"); return; }
    setSaving(true); setError("");
    const payload = {
      product_code: form.product_code,
      source: form.source || null,
      estimated_value: form.estimated_value === "" ? null : Number(form.estimated_value),
      probability: form.probability === "" ? null : Number(form.probability),
      next_action: form.next_action || null,
      next_action_date: form.next_action_date ? new Date(form.next_action_date).toISOString() : null,
    };
    if (needsOverride) payload.override_reason = override;
    try {
      const res = await opportunitiesApi.create(orgId, payload);
      toast.success("Opportunity created");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      if (isSuppressionOverride(err)) { setNeedsOverride(true); setError(""); }
      else setError(apiErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="opportunity-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add opportunity</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Product">
            <SelectInput testid="opp-product" value={form.product_code} onChange={set("product_code")}>
              <option value="">Choose product…</option>
              {products.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
            </SelectInput>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source"><TextInput testid="opp-source" value={form.source} onChange={set("source")} /></Field>
            <Field label="Est. value (£)"><TextInput testid="opp-value" type="number" value={form.estimated_value} onChange={set("estimated_value")} /></Field>
          </div>
          <Field label="Probability % (manual)">
            <TextInput testid="opp-probability" type="number" min="0" max="100" value={form.probability} onChange={set("probability")} placeholder="Manually estimated only — no AI" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Next action"><TextInput testid="opp-next-action" value={form.next_action} onChange={set("next_action")} /></Field>
            <Field label="Next action date"><TextInput testid="opp-next-date" type="datetime-local" value={form.next_action_date} onChange={set("next_action_date")} /></Field>
          </div>
          {needsOverride && <OverrideNotice value={override} onChange={setOverride} entity="opportunity" />}
          {error && <p data-testid="opp-form-error" className="text-sm font-medium text-rose-600">{error}</p>}
          <button type="submit" disabled={saving || (needsOverride && !override.trim())} data-testid="opp-form-submit"
            className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
            {saving ? "Saving…" : needsOverride ? "Proceed with override" : "Create opportunity"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
